import axios, { AxiosResponse, AxiosError } from 'axios';
import toast from 'react-hot-toast';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Retry configuration
interface RetryConfig {
  retries: number;
  retryDelay: number;
  retryCondition?: (error: AxiosError) => boolean;
}

const defaultRetryConfig: RetryConfig = {
  retries: 3,
  retryDelay: 1000, // 1 second base delay
  retryCondition: (error: AxiosError) => {
    // Retry on network errors or 5xx server errors
    return !error.response || (error.response.status >= 500 && error.response.status < 600);
  },
};

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000, // 10 second timeout
});

// Retry mechanism with exponential backoff
const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const axiosRetry = async <T>(
  requestFn: () => Promise<AxiosResponse<T>>,
  config: RetryConfig = defaultRetryConfig
): Promise<AxiosResponse<T>> => {
  let lastError: AxiosError | undefined;

  for (let attempt = 0; attempt <= config.retries; attempt++) {
    try {
      return await requestFn();
    } catch (error) {
      lastError = error as AxiosError;

      // Don't retry if it's the last attempt or if retry condition is not met
      if (attempt === config.retries || !config.retryCondition?.(lastError)) {
        throw lastError;
      }

      // Exponential backoff: delay increases with each attempt
      const delay = config.retryDelay * Math.pow(2, attempt);
      await sleep(delay);
    }
  }

  throw new Error(lastError?.message || 'Request failed after retries');
};

// Request interceptor for adding auth tokens
api.interceptors.request.use(
  (config) => {
    // Get auth token from localStorage (matches AuthContext key)
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error: AxiosError) => {
    // Handle common errors
    if (error.response) {
      const status = error.response.status;
      const message = (error.response.data as any)?.detail || error.message;

      switch (status) {
        case 400:
          toast.error(`Bad Request: ${message}`);
          break;
        case 401:
          toast.error('Session expired. Please log in again.');
          // Clear invalid token and redirect to login
          localStorage.removeItem('token');
          window.location.href = '/login';
          break;
        case 403:
          toast.error('Access forbidden');
          break;
        case 404:
          toast.error('Resource not found');
          break;
        case 422:
          toast.error(`Validation Error: ${message}`);
          break;
        case 500:
          toast.error('Internal server error');
          break;
        default:
          toast.error(`Error: ${message}`);
      }
    } else if (error.request) {
      toast.error('Network error - please check your connection');
    } else {
      toast.error('An unexpected error occurred');
    }

    console.error('API Error:', error);
    return Promise.reject(error);
  }
);

// Enhanced API utility functions with retry mechanism
export const apiClient = {
  // Generic GET request with retry
  get: async <T>(
    url: string,
    params?: Record<string, any>,
    retryConfig?: Partial<RetryConfig>
  ): Promise<T> => {
    const response = await axiosRetry(
      () => api.get<T>(url, {
        params,
        headers: { 'Content-Type': 'application/json' }
      }),
      { ...defaultRetryConfig, ...retryConfig }
    );
    return response.data;
  },

  // Generic POST request with retry
  post: async <T>(
    url: string,
    data?: any,
    retryConfig?: Partial<RetryConfig>
  ): Promise<T> => {
    const response = await axiosRetry(
      () => api.post<T>(url, data, {
        headers: { 'Content-Type': 'application/json' }
      }),
      { ...defaultRetryConfig, ...retryConfig }
    );
    return response.data;
  },

  // Generic PUT request with retry
  put: async <T>(
    url: string,
    data?: any,
    retryConfig?: Partial<RetryConfig>
  ): Promise<T> => {
    const response = await axiosRetry(
      () => api.put<T>(url, data, {
        headers: { 'Content-Type': 'application/json' }
      }),
      { ...defaultRetryConfig, ...retryConfig }
    );
    return response.data;
  },

  // Generic DELETE request with retry
  delete: async <T>(
    url: string,
    retryConfig?: Partial<RetryConfig>
  ): Promise<T> => {
    const response = await axiosRetry(
      () => api.delete<T>(url, {
        headers: { 'Content-Type': 'application/json' }
      }),
      { ...defaultRetryConfig, ...retryConfig }
    );
    return response.data;
  },

  // Generic PATCH request with retry
  patch: async <T>(
    url: string,
    data?: any,
    retryConfig?: Partial<RetryConfig>
  ): Promise<T> => {
    const response = await axiosRetry(
      () => api.patch<T>(url, data, {
        headers: { 'Content-Type': 'application/json' }
      }),
      { ...defaultRetryConfig, ...retryConfig }
    );
    return response.data;
  },

  // Utility to check if a feature/endpoint is available
  healthCheck: async (endpoint: string): Promise<boolean> => {
    try {
      await api.get(`${endpoint}/health`, { timeout: 3000 });
      return true;
    } catch {
      return false;
    }
  },
};

// Note: Individual API services are exported from their own files to avoid circular dependencies

export default api;